import { useContext, useState } from "react";
import ThemeContext from "../../lib/theme-context";
import EventBadge from "./EventBadge";
import moment from "moment";

const CalendarListView = ({
  startDate,
  endDate,
  events,
  onEventClick,
  onDragStart,
  currentMonth,
  onMonthChange,
}) => {
  const theme = useContext(ThemeContext);
  const isDark = theme[0] === "dark";
  const [expandedDates, setExpandedDates] = useState(new Set());

  const eventsByDate = {};
  events.forEach((event) => {
    const eventDate = moment(event.date);
    // Only include events within the current month date range
    if (eventDate.isSameOrAfter(startDate, "day") && eventDate.isSameOrBefore(endDate, "day")) {
      const dateKey = event.date;
      if (!eventsByDate[dateKey]) {
        eventsByDate[dateKey] = [];
      }
      eventsByDate[dateKey].push(event);
    }
  });

  const dates = [];
  let currentDate = moment(startDate);
  while (currentDate.isSameOrBefore(endDate, "day")) {
    dates.push(currentDate.clone());
    currentDate.add(1, "day");
  }
  dates.reverse();

  const toggleDate = (dateKey) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(dateKey)) {
      newExpanded.delete(dateKey);
    } else {
      newExpanded.add(dateKey);
    }
    setExpandedDates(newExpanded);
  };

  return (
    <div
      style={{
        padding: "0 16px 32px 16px",
        backgroundColor: isDark ? "#1a1b1e" : "#f9fafb",
        minHeight: "200px",
      }}
    >
      {/* Navigation Header */}
      {onMonthChange && currentMonth && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px", paddingTop: "0" }}>
          {/* Date title */}
          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: isDark ? "#e5e7eb" : "#374151",
              textAlign: "center",
            }}
          >
            {moment(currentMonth).format("MMMM YYYY")}
          </div>
          {/* Navigation buttons */}
          <div style={{ display: "flex", gap: "8px", justifyContent: "center"}}>
            <button
              onClick={() => {
                const newMonth = moment(currentMonth).subtract(1, "month");
                onMonthChange(newMonth);
              }}
              style={{
                padding: "4px 8px",
                backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
                color: isDark ? "#e5e7eb" : "#374151",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "11px",
                transition: "all 0.2s ease",
                flex: 1,
              }}
            >
              Previous
            </button>
            <button
              onClick={() => {
                onMonthChange(moment());
              }}
              style={{
                padding: "4px 8px",
                backgroundColor: moment().isSame(currentMonth, "month")
                  ? isDark ? "rgba(55, 136, 216, 0.2)" : "rgba(55, 136, 216, 0.15)"
                  : isDark ? "#2d2e33" : "#e5e7eb",
                color: moment().isSame(currentMonth, "month") ? "#3788d8" : isDark ? "#e5e7eb" : "#374151",
                border: "2px solid transparent",
                borderColor: moment().isSame(currentMonth, "month") ? "#3788d8" : "transparent",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "11px",
                transition: "all 0.2s ease",
                flex: 1,
              }}
            >
              Today
            </button>
            <button
              onClick={() => {
                const newMonth = moment(currentMonth).add(1, "month");
                onMonthChange(newMonth);
              }}
              style={{
                padding: "4px 8px",
                backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
                color: isDark ? "#e5e7eb" : "#374151",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "11px",
                transition: "all 0.2s ease",
                flex: 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {Object.keys(eventsByDate).length === 0 ? (
        <div
          style={{
            padding: "48px 32px",
            textAlign: "center",
            backgroundColor: isDark ? "#1e1f23" : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
            margin: "16px 0",
          }}
        >
          <div
            style={{
              color: isDark ? "#e5e7eb" : "#6b7280",
              fontSize: "16px",
              fontWeight: "500",
            }}
          >
            No events this month
          </div>
        </div>
      ) : (
        dates.map((date) => {
          const dateKey = date.format("YYYY-MM-DD");
          const dayEvents = eventsByDate[dateKey] || [];
          const isToday = date.isSame(moment(), "day");
          const isExpanded = expandedDates.has(dateKey) || dayEvents.length > 0;

          if (dayEvents.length === 0) return null;

          return (
            <div
              key={dateKey}
              style={{
                marginBottom: "8px",
                backgroundColor: isDark ? "#1e1f23" : "#ffffff",
                border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
                borderLeft: isToday ? `4px solid #3788d8` : undefined,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <div
                onClick={() => toggleDate(dateKey)}
                style={{
                  padding: "12px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: isDark ? "#1e1f23" : "#ffffff",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: isToday
                      ? "#3788d8"
                      : isDark
                        ? "#e5e7eb"
                        : "#374151",
                  }}
                >
                  {date.format("ddd, MMM D")}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: isDark ? "#9ca3af" : "#6b7280",
                  }}
                >
                  {dayEvents.length}
                </div>
              </div>

            {isExpanded && (
              <div
                style={{
                  padding: "8px 12px 12px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  borderTop: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
                }}
              >
                {dayEvents.map((event, index) => {
                  const isDraggable =
                    (event.category === "forecast" ||
                    event.category === "reflection") &&
                    event.category !== "holiday";

                  return (
                    <EventBadge
                      key={`${event.publicId}-${index}`}
                      event={event}
                      onClick={onEventClick}
                      isDraggable={isDraggable}
                      onDragStart={onDragStart}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })
      )}
    </div>
  );
};

export default CalendarListView;
